import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import '../styles.css';

const Dashboard = () => {
  const [userData, setUserData] = useState(null);
  const [stageData, setStageData] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const [referralRewards, setReferralRewards] = useState([]);
  const [walletAddress, setWalletAddress] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const lastFetchTimeRef = useRef(0); // Track last fetch time for debouncing

  const TOTAL_SUPPLY = 500000000; // Adjust to your stage’s total supply

  // Hardcode stage dates and prices (matching Presale.js)
  const stageDates = useMemo(() => [
    { id: 1, start: new Date('2025-03-18T00:00:00Z'), end: new Date('2025-04-16T23:59:59Z'), name: 'Stage 1', usdcPrice: 0.0050 },
    { id: 2, start: new Date('2025-04-17T00:00:00Z'), end: new Date('2025-11-10T23:59:59Z'), name: 'Stage 2', usdcPrice: 0.0100 },
    { id: 3, start: new Date('2025-11-11T00:00:00Z'), end: new Date('2026-12-28T23:59:59Z'), name: 'Stage 3', usdcPrice: 0.0150 },
  ], []); // Empty dependency array since the dates are static

  const fetchData = useCallback(async () => {
    const now = Date.now();
    // Debounce: Only fetch if at least 5 seconds have passed since the last fetch
    if (now - lastFetchTimeRef.current < 5000) {
      return;
    }
    lastFetchTimeRef.current = now;

    const token = localStorage.getItem('token');
    console.log('Token used in Dashboard:', token); // Debug log
    if (!token) {
      console.log('No token found, redirecting to login');
      navigate('/login');
      return;
    }
    setLoading(true);
    try {
      console.log('Fetching user data from /api/user/dashboard');
      const userRes = await axios.get(`${process.env.REACT_APP_API_URL}/api/user/dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 5000,
      });
      console.log('Fetched userData:', userRes.data);
      setUserData(userRes.data);

      console.log('Fetching stage data from /api/stages');
      const stageRes = await axios.get(`${process.env.REACT_APP_API_URL}/api/stages`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 5000,
      });
      console.log('Fetched stageData:', stageRes.data);

      console.log('Fetching purchases from /api/user/purchases');
      const purchasesRes = await axios.get(`${process.env.REACT_APP_API_URL}/api/user/purchases`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 5000,
      });
      console.log('Fetched purchases:', purchasesRes.data);
      setPurchases(purchasesRes.data);

      const nowDate = new Date();
      const activeStage = stageDates.find((stage) => nowDate >= stage.start && nowDate <= stage.end);
      const currentStageData = stageRes.data.find((s) => s.stage_id === activeStage?.id) || stageRes.data[0];
      setStageData(currentStageData);

      setWalletAddress(userRes.data.wallet_address || '');

      // Fetch referral rewards after other data is loaded
      console.log('Fetching referral rewards from /api/user/referral-rewards');
      let retries = 3;
      let referralRewardsData = [];
      while (retries > 0) {
        try {
          const referralRewardsRes = await axios.get(`${process.env.REACT_APP_API_URL}/api/user/referral-rewards`, {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 5000,
          });
          console.log('Referral rewards response status:', referralRewardsRes.status);
          console.log('Referral rewards response headers:', referralRewardsRes.headers);
          console.log('Referral rewards response data:', referralRewardsRes.data);
          referralRewardsData = referralRewardsRes.data;
          break; // Exit retry loop on success
        } catch (referralErr) {
          console.error('Error fetching referral rewards (attempt', 4 - retries, '):', {
            message: referralErr.message,
            response: referralErr.response ? {
              status: referralErr.response.status,
              data: referralErr.response.data,
              headers: referralErr.response.headers,
            } : 'No response data',
            config: referralErr.config,
            code: referralErr.code,
          });
          retries--;
          if (retries === 0) {
            console.error('All retries failed for referral rewards fetch');
            referralRewardsData = []; // Fallback to empty array
          } else {
            console.log('Retrying referral rewards fetch...');
            await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second before retrying
          }
        }
      }
      setReferralRewards(referralRewardsData);
    } catch (err) {
      console.error('Fetch error in Dashboard:', err.response ? err.response.data : err.message);
      if (err.response?.status === 401) {
        console.log('Invalid token detected in Dashboard, redirecting to login');
        localStorage.removeItem('token'); // Clear invalid token
        navigate('/login');
      } else {
        setError(err.response?.data?.error || 'Failed to load dashboard data. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate, stageDates]);

  // Initial fetch on mount
  useEffect(() => {
    console.log('Refreshing Dashboard data due to navigation to:', location.pathname);
    fetchData();
  }, [fetchData, location.pathname]);

  // Additional refresh for post-transaction updates
  useEffect(() => {
    console.log('Location state:', location.state);
    if (location.state?.fromTransaction) {
      console.log('Refreshing Dashboard data due to transaction update');
      fetchData(); // Refresh data after transaction
    }
  }, [location, fetchData]);

  // Generate referral code if needed
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (userData && !userData.referral_code && Number(userData.total_tokens_purchased) > 1 && token) {
      axios.post(
        `${process.env.REACT_APP_API_URL}/api/user/generate-referral`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      )
        .then((res) => {
          console.log('Referral code generated:', res.data);
          fetchData(); // Refresh user data to include new referral_code
        })
        .catch((err) => {
          console.error('Failed to generate referral code:', err);
          setError('Failed to generate referral code');
        });
    }
  }, [userData, navigate, fetchData]);

  // Automatic stage update every minute
  useEffect(() => {
    const stageCheckInterval = setInterval(() => {
      const nowDate = new Date();
      const activeStage = stageDates.find((stage) => nowDate >= stage.start && nowDate <= stage.end);
      const currentStageId = stageData?.stage_id;
      if (activeStage && activeStage.id !== currentStageId) {
        fetchData(); // Refresh data if the stage has changed
      }
    }, 60000); // Check every minute

    return () => {
      clearInterval(stageCheckInterval);
    };
  }, [stageData, fetchData, stageDates]);

  const handleWalletSubmit = async () => {
    const token = localStorage.getItem('token');
    setError('');
    setLoading(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/user/update-wallet`,
        { wallet_address: walletAddress },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUserData({ ...userData, wallet_address: walletAddress });
      setIsEditing(false);
    } catch (err) {
      setError(err.response?.data?.error || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = () => setIsEditing(true);

  const handleCopyReferral = () => {
    if (userData?.referral_code) {
      const referralLink = `${window.location.origin}/register?ref=${userData.referral_code}`;
      navigator.clipboard
        .writeText(referralLink)
        .then(() => alert('Referral link copied to clipboard!'))
        .catch((err) => setError('Failed to copy referral link: ' + err.message));
    }
  };

  const formatNumber = (num) => {
    return parseFloat(num).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Calculate per-stage totals for purchased, investment, and current amount (excluding referral rewards)
  const stageTotals = useMemo(() => {
    const totals = {
      1: { purchased: 0, referral: 0, investment: 0, currentAmount: 0 },
      2: { purchased: 0, referral: 0, investment: 0, currentAmount: 0 },
      3: { purchased: 0, referral: 0, investment: 0, currentAmount: 0 },
    };

    // Aggregate purchased tokens and investment (amount) by stage
    console.log('Aggregating purchases:', purchases);
    purchases.forEach(purchase => {
      if (purchase.transaction_status.toLowerCase() === 'success') {
        const stageId = Number(purchase.stage_id); // Ensure stage_id is a number
        if (totals[stageId]) {
          totals[stageId].purchased += Number(purchase.tokens_purchased || 0);
          totals[stageId].investment += Number(purchase.amount || 0);
        } else {
          console.warn(`Invalid stage_id ${stageId} in purchase`, purchase);
        }
      } else {
        console.log(`Skipping purchase with transaction_status ${purchase.transaction_status}`, purchase);
      }
    });

    // Calculate current amount per stage using the current stage's price (excluding referral rewards for now)
    const nowDate = new Date();
    const activeStage = stageDates.find((stage) => nowDate >= stage.start && nowDate <= stage.end);
    const currentPrice = activeStage?.usdcPrice || stageDates[0].usdcPrice; // Fallback to Stage 1 price if no active stage
    Object.keys(totals).forEach(stageId => {
      const stageTotalTokens = totals[stageId].purchased; // Only purchased tokens for now
      totals[stageId].currentAmount = stageTotalTokens * currentPrice;
    });

    console.log('Stage totals (before referral rewards):', totals);
    return totals;
  }, [purchases, stageDates]);

  // Create a mapping of stage_id to referral rewards for direct display
  const referralRewardsByStage = useMemo(() => {
    const rewards = {
      1: 0,
      2: 0,
      3: 0,
    };

    if (referralRewards.length > 0) {
      referralRewards.forEach(reward => {
        const stageId = Number(reward.stage_id);
        if (rewards[stageId] !== undefined) {
          rewards[stageId] = Number(reward.total_reward_tokens || 0);
        } else {
          console.warn(`Invalid stage_id ${stageId} in referral rewards`, reward);
        }
      });
    } else {
      // Fallback: Assign userData.total_referral_reward_tokens to Stage 1 only
      console.warn('Referral rewards fetch failed, falling back to userData.total_referral_reward_tokens');
      const totalReferralFromUserData = Number(userData?.total_referral_reward_tokens || 0);
      rewards[1] = totalReferralFromUserData; // Assign all to Stage 1 as a fallback
    }

    return rewards;
  }, [referralRewards, userData]);

  // Update stageTotals to include referral rewards for the first table
  const stageTotalsWithReferral = useMemo(() => {
    const totals = { ...stageTotals }; // Clone the stageTotals

    // Add referral rewards to the totals for the first table
    Object.keys(totals).forEach(stageId => {
      totals[stageId].referral = referralRewardsByStage[stageId];
      // Recalculate current amount including referral rewards
      const stageTotalTokens = totals[stageId].purchased + totals[stageId].referral;
      const nowDate = new Date();
      const activeStage = stageDates.find((stage) => nowDate >= stage.start && nowDate <= stage.end);
      const currentPrice = activeStage?.usdcPrice || stageDates[0].usdcPrice;
      totals[stageId].currentAmount = stageTotalTokens * currentPrice;
    });

    console.log('Final stage totals (with referral rewards):', totals);
    return totals;
  }, [stageTotals, referralRewardsByStage, stageDates]);

  // Calculate total investment ("You Invested") across all stages
  const totalInvestment = useMemo(() => {
    return purchases.reduce((sum, purchase) => {
      if (purchase.transaction_status.toLowerCase() === 'success') {
        return sum + Number(purchase.amount || 0);
      }
      return sum;
    }, 0);
  }, [purchases]);

  // Calculate total current amount using the current stage's price (should match the sum of per-stage current amounts)
  const totalCurrentAmount = useMemo(() => {
    return Object.values(stageTotalsWithReferral).reduce((sum, stage) => sum + stage.currentAmount, 0);
  }, [stageTotalsWithReferral]);

  // Calculate total purchased and referral rewards for summary row
  const totalPurchasedAllStages = Object.values(stageTotalsWithReferral).reduce((sum, stage) => sum + stage.purchased, 0);
  const totalReferralAllStages = Object.values(referralRewardsByStage).reduce((sum, reward) => sum + reward, 0);
  const totalAllStages = totalPurchasedAllStages + totalReferralAllStages;

  if (loading) return <div className="content-container container my-4 text-center">Loading...</div>;
  if (error) return <div className="content-container container my-4 text-center text-danger">{error}</div>;
  if (!userData || !stageData) return null; // Prevent rendering until data is loaded

  const progressPercentage = stageData ? Math.min((stageData.tokens_sold / TOTAL_SUPPLY) * 100, 100) : 0;

  // Calculate totals for the summary row
  const totalTokensToReceive = (Number(userData.total_tokens_purchased || 0) + Number(userData.total_referral_reward_tokens || 0));

  return (
    <div className="page-container">
      <Header />
      <section className="content-container container my-4">
        <h1 className="text-center mb-4 text-white">Welcome, {userData.full_name}</h1>
        {error && <p className="text-danger text-center">{error}</p>}

        <div className="card-dashboard p-4 mx-auto card-max-width mb-4">
          <h3>Total Tokens Sold (Stage {stageData.stage_id})</h3>
          <div className="progress mb-2">
            <div
              className="progress-bar bg-success"
              role="progressbar"
              style={{ width: `${progressPercentage}%` }}
              aria-valuenow={progressPercentage}
              aria-valuemin="0"
              aria-valuemax="100"
            >
              {progressPercentage.toFixed(2)}%
            </div>
          </div>
          <h5 className="text-center text-success">
            {formatNumber(stageData.tokens_sold)} / {formatNumber(TOTAL_SUPPLY)} Tokens Sold
          </h5>
        </div>

        {/* Existing Table: Your Investment Overview */}
        <div className="card-dashboard p-4 mx-auto card-max-width mb-4">
          <h3 className="text-center mb-4">Your Investment Overview</h3>
          <div className="table-responsive">
            <table className="table table-bordered table-striped investment-table">
              <thead className="thead-dark">
                <tr>
                  <th>Stage</th>
                  <th>Purchased (ABAY)</th>
                  <th>Referral Rewards (ABAY)</th>
                  <th>Total (ABAY)</th>
                  <th>You Invested (USD)</th>
                  <th>Current Amount (USD)</th>
                </tr>
              </thead>
              <tbody>
                {/* Stage 1 Row */}
                <tr>
                  <td>1</td>
                  <td className="text-right">{formatNumber(stageTotalsWithReferral[1].purchased)}</td>
                  <td className="text-right">{formatNumber(referralRewardsByStage[1])}</td>
                  <td className="text-right">{formatNumber(stageTotalsWithReferral[1].purchased + referralRewardsByStage[1])}</td>
                  <td className="text-right">${formatNumber(stageTotalsWithReferral[1].investment)}</td>
                  <td className="text-right">${formatNumber(stageTotalsWithReferral[1].currentAmount)}</td>
                </tr>
                {/* Stage 2 Row */}
                <tr>
                  <td>2</td>
                  <td className="text-right">{formatNumber(stageTotalsWithReferral[2].purchased)}</td>
                  <td className="text-right">{formatNumber(referralRewardsByStage[2])}</td>
                  <td className="text-right">{formatNumber(stageTotalsWithReferral[2].purchased + referralRewardsByStage[2])}</td>
                  <td className="text-right">${formatNumber(stageTotalsWithReferral[2].investment)}</td>
                  <td className="text-right">${formatNumber(stageTotalsWithReferral[2].currentAmount)}</td>
                </tr>
                {/* Stage 3 Row */}
                <tr>
                  <td>3</td>
                  <td className="text-right">{formatNumber(stageTotalsWithReferral[3].purchased)}</td>
                  <td className="text-right">{formatNumber(referralRewardsByStage[3])}</td>
                  <td className="text-right">{formatNumber(stageTotalsWithReferral[3].purchased + referralRewardsByStage[3])}</td>
                  <td className="text-right">${formatNumber(stageTotalsWithReferral[3].investment)}</td>
                  <td className="text-right">${formatNumber(stageTotalsWithReferral[3].currentAmount)}</td>
                </tr>
                {/* Total Row */}
                <tr className="table-success">
                  <td><strong>Total</strong></td>
                  <td className="text-right"><strong>{formatNumber(totalPurchasedAllStages)}</strong></td>
                  <td className="text-right"><strong>{formatNumber(totalReferralAllStages)}</strong></td>
                  <td className="text-right"><strong>{formatNumber(totalAllStages)}</strong></td>
                  <td className="text-right"><strong>${formatNumber(totalInvestment)}</strong></td>
                  <td className="text-right"><strong>${formatNumber(totalCurrentAmount)}</strong></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        {/* Existing Cards for Other Metrics */}
        <div className="row mb-2">
          <div className="col-md-4">
            <div className="card-dashboard p-3">
              <h5>Total Tokens to Receive</h5>
              <h5><span className="text-success text-center">{formatNumber(totalTokensToReceive)}</span></h5>
            </div>
          </div>
          <div className="col-md-4">
            <div className="card-dashboard p-3">
              <h5>Total Tokens Staked</h5>
              <h5><span className="text-success text-center">{formatNumber(userData.total_staked || 0.0)}</span></h5>
            </div>
          </div>
          <div className="col-md-4">
            <div className="card-dashboard p-3">
              <h5>Total Tokens Released</h5>
              <h5><span className="text-success text-center">{formatNumber(userData.total_released || 0.0)}</span></h5>
            </div>
          </div>
          <div className="col-md-4">
            <div className="card-dashboard p-3">
              <h5>Total Staking Reward</h5>
              <h5><span className="text-success text-center">{formatNumber(userData.total_staking_reward || 0.0)}</span></h5>
            </div>
          </div>
        </div>

        <div className="card p-2 mx-auto card-max-width">
          <h5>{userData.wallet_address && !isEditing ? 'Wallet Address' : 'Add/Edit Wallet Address'}</h5>
          {userData.wallet_address && !isEditing ? (
            <div className="mt-3">
              <h5 className="text-success wallet-input">{userData.wallet_address}</h5>
              <button onClick={handleEditClick} className="btn btn-primary">Edit Wallet Address</button>
            </div>
          ) : (
            <div className="mt-3">
              <input
                type="text"
                className="form-control mb-3"
                value={walletAddress}
                onChange={(e) => setWalletAddress(e.target.value)}
                placeholder="Solana Wallet Address"
              />
              <button
                onClick={handleWalletSubmit}
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? 'Saving...' : userData.wallet_address ? 'Update Wallet' : 'Add Wallet'}
              </button>
            </div>
          )}
        </div>

        <div className="card p-4 mt-4 mx-auto card-max-width">
          <h5>Your Referral Link</h5>
          {Number(userData.total_tokens_purchased) > 1 && userData.referral_code ? (
            <div className="d-flex justify-content-between align-items-center">
              <p className="mb-0">{`${window.location.origin}/register?ref=${userData.referral_code}`}</p>
              <button
                onClick={handleCopyReferral}
                className="btn btn-success"
                disabled={loading}
              >
                Copy
              </button>
            </div>
          ) : (
            <div className="text-muted">will be available after purchasing the minimum amount of ABAY Token.</div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default Dashboard;
